import { FC, lazy, ReactNode, Suspense, useLayoutEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Header from 'components/header/Header';
import Loader from 'components/common/loader/Loader';
import useShareModalStore from 'store/useShareModalStore';
import { shallow } from 'zustand/shallow';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import BuildWiseLoader from 'routes/BuildWiseLoader';
import { EditProfileModal } from '@buildwise/libs/people';
import useLayoutStore from 'store/useLayoutStore';

const InviteUserModal = lazy(() => import('components/modals/inviteUserModal/InviteUserModal'));
const ReportModal = lazy(() => import('components/modals/reportModal/ReportModal'));
const ShareModal = lazy(() => import('components/modals/shareModal/ShareModal'));

export const $Root = styled(Box, { label: 'Root' })(({ theme }) => ({
  width: '100%',
  margin: '0 auto',
  minHeight: theme.vh100,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}));

export const $Container = styled(Box, { label: 'ContentBox' })(({ theme }) => ({
  margin: '0 auto',
  padding: '16px 48px 0',
  width: '100%',
  display: 'flex',
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    padding: '16px 24px 16px 0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 8px 0',
  },
}));

export const $ContentBox = styled('main', { label: 'ContentBox' })(({ theme }) => ({
  flexGrow: 1,
  position: 'relative',
  width: '70%',
}));

type MenuWrapperProps = {
  sidebar: FC;
  children: ReactNode;
};

const MainWrapper: FC<MenuWrapperProps> = ({ sidebar: Sidebar, children }) => {
  const { shareData } = useShareModalStore((state) => ({ ...state }), shallow);
  const openShareModal = useMemo(() => !!shareData, [shareData]);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const { companyId, user, company } = useUserData();
  const { activeUniqueEntries: customUserInputFields, isLoading: isLoadingUserInputFields } =
    useConfigQuery({
      companyId,
      configName: CONFIGS.USER_INPUT_FIELDS.configName,
      shouldFetch: !!company,
    });
  const { isFullScreen } = useLayoutStore();

  useLayoutEffect(() => {
    if (customUserInputFields.length) {
      const requiredFieldKeys = ['firstname', 'lastname', 'email'];
      customUserInputFields.forEach((field) => {
        if (field.referenceObject?.isRequired) {
          requiredFieldKeys.push(field.entryCode);
        }
      });

      const isProfileNotCompleted = requiredFieldKeys.some((field) => {
        const value = user?.[field] || user?.customFields?.[field];

        return Array.isArray(value) ? value.length === 0 : !value;
      });

      if (isProfileNotCompleted) {
        setOpenEditProfile(true);
      }
    }
  }, [customUserInputFields, user]);

  if (isLoadingUserInputFields) {
    return <BuildWiseLoader />;
  }

  return (
    <$Root>
      <Header />
      <$Container
        sx={(theme) => ({
          maxWidth: isFullScreen ? '100vw' : theme.maxWidth,
          padding: isFullScreen ? '16px 24px 0' : '16px 48px 0',
          margin: isFullScreen ? 0 : '0 auto',
        })}
      >
        <Sidebar />
        <$ContentBox>{children}</$ContentBox>
      </$Container>

      <Suspense fallback={<Loader variant="fixed" />}>
        {openShareModal && <ShareModal />}
        <ReportModal />
        <InviteUserModal />
      </Suspense>

      {openEditProfile && !!user && (
        <EditProfileModal
          open={openEditProfile}
          setOpen={setOpenEditProfile}
          user={user}
          showAsWelcomeBanner={!user.lastLoggedInAt}
          hideCloseBtn
        />
      )}
    </$Root>
  );
};

export default MainWrapper;
